"use client";

import Image from "next/image";
import { Icons } from "@/components/icons";
import {
  INTEGRATIONS,
  WEBSITE_PLATFORMS,
} from "../_data/page-data";
import {
  AnimatedSection,
  StaggerGrid,
  FadeInChild,
  GlowCard,
} from "./motion";

export function IntegrationsSection() {
  return (
    <AnimatedSection id="integrations" className="container py-16 md:py-24 lg:py-32">
      <div className="mx-auto flex max-w-[58rem] flex-col items-center space-y-4 text-center">
        <span className="inline-flex items-center rounded-full bg-emerald-50 px-4 py-1 text-xs font-semibold text-emerald-600 ring-1 ring-inset ring-emerald-200">
          Integrations
        </span>
        <h2 className="font-heading text-3xl leading-[1.1] sm:text-3xl md:text-6xl">
          Meet your users <span className="text-gradient">where they are</span>
        </h2>
        <p className="max-w-[600px] leading-relaxed text-muted-foreground sm:text-lg">
          Connect once. Deploy everywhere. Your chatbots answer on every platform — automatically.
        </p>
      </div>

      {/* Messaging platforms */}
      <StaggerGrid className="mx-auto mt-12 grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {INTEGRATIONS.map((p) => {
          const PIcon = Icons[p.iconKey as keyof typeof Icons];
          return (
            <FadeInChild key={p.name}>
              <GlowCard className="h-full">
                <div
                  className={`absolute -right-4 -top-4 h-24 w-24 rounded-full bg-gradient-to-br ${p.gradient} opacity-[0.06] blur-xl transition-all group-hover:opacity-[0.12] group-hover:scale-150`}
                />
                <div className="relative p-6 space-y-4">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${p.bg} transition-transform group-hover:scale-110`}>
                    <PIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">{p.name}</h3>
                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                  </div>
                  <div className="pt-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full bg-gradient-to-r ${p.gradient} px-3 py-1 text-[11px] font-semibold text-white`}
                    >
                      <Icons.check className="h-3 w-3" />
                      Ready to connect
                    </span>
                  </div>
                </div>
              </GlowCard>
            </FadeInChild>
          );
        })}
      </StaggerGrid>

      {/* Website platforms */}
      <div className="mx-auto mt-10 max-w-4xl">
        <p className="mb-4 text-center text-xs font-medium uppercase tracking-widest text-slate-400">
          Also embeds on
        </p>
        <StaggerGrid className="grid w-full items-center justify-center gap-6 grid-cols-2 md:grid-cols-4">
          {WEBSITE_PLATFORMS.map((ws) => (
            <FadeInChild key={ws.name}>
              <div className="mx-auto flex w-full items-center justify-center rounded-2xl border border-slate-200/60 bg-white/60 backdrop-blur-sm p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <Image
                  alt={ws.name}
                  className="aspect-[2/1] overflow-hidden object-contain object-center opacity-70 hover:opacity-100 transition-opacity"
                  height={60}
                  src={ws.src}
                  width={120}
                />
              </div>
            </FadeInChild>
          ))}
        </StaggerGrid>
      </div>
    </AnimatedSection>
  );
}
